import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../../lib/api.js';
import WatchEventDetails from '../../components/WatchEventDetails.jsx';
import SectionCard from '../../components/SectionCard.jsx';
import StatusWord, { SeverityWord } from '../../components/StatusWord.jsx';
import { useUnsavedChanges } from '../../lib/use-unsaved-changes.js';
import { CASE_OUTCOME_LABEL, CASE_STATE_LABEL, activityPath, formatWhen } from '../../lib/watch-format.js';

const OWN_CHANGE_TEXT = 'You cannot change a case about your own change.';
const NOT_TOLD_TEXT = 'The case was updated, but your guardians could not be told.';
const TOLD_TEXT = 'Case updated. Your guardians were told.';

// The portal words its own-change refusal for the API; here it is said in plain words.
function changeError(e) {
  if (e?.status === 403 && /own change/i.test(e?.message ?? '')) return OWN_CHANGE_TEXT;
  return e?.message || 'The case could not be changed.';
}

// ENTRAID-347: an administrator who did not receive the notice answered No (the portal sends the flag; the words match the email).
const REPORTED_BY_ADMIN = 'Reported by an admin';
function ReportedByAdmin() {
  return <span className="rounded border border-ipc-border px-1.5 text-[12px] font-semibold text-ipc-muted" data-testid="reported-by-admin">{REPORTED_BY_ADMIN}</span>;
}

const caseLine = (c) => [CASE_STATE_LABEL[c.state] ?? c.state, c.outcome ? (CASE_OUTCOME_LABEL[c.outcome] ?? c.outcome) : null].filter(Boolean).join(', ');

// The Flagged tab of Activity (ENTRAID-305, moved from Notifications by the ENTRAID-347 redesign, plan C3).
// Admin-only: the portal refuses cases to every other role, and the section does not route operators here.
const CASE_TONE = Object.freeze({ open: 'bad', acknowledged: 'warn', resolved: 'good' });
export default function WatchCases({ customerId }) {
  const [state, setState] = useState('');
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [failedAfter, setFailedAfter] = useState(null);
  // Only the newest request may change what is shown.
  const reqRef = useRef(0);
  const stateId = useId();

  const load = useCallback(async (after) => {
    const req = ++reqRef.current;
    setLoading(true);
    setError(null);
    try {
      const r = await api.listWatchCases(customerId, { ...(state ? { state } : {}), ...(after ? { cursor: after } : {}) });
      if (reqRef.current !== req) return;
      setItems((prev) => (after ? [...prev, ...(r.items ?? [])] : (r.items ?? [])));
      setCursor(r.cursor ?? null);
      setLoaded(true);
    } catch (e) {
      if (reqRef.current !== req) return;
      setError(e?.message || 'Cases could not be loaded.');
      setFailedAfter(after ?? null);
    } finally {
      if (reqRef.current === req) setLoading(false);
    }
  }, [customerId, state]);
  useEffect(() => {
    setItems([]);
    setCursor(null);
    setLoaded(false);
    load(null);
    return () => { reqRef.current += 1; };
  }, [load]);

  return (
    <SectionCard
      title="Flagged"
      description="Events someone answered as not expected, followed up as cases."
      action={(
        <span className="flex items-center gap-2 font-normal">
          <label className="text-[13px] text-ipc-muted" htmlFor={stateId}>State</label>
          <select id={stateId} className="input w-40 py-1.5" value={state} onChange={(e) => setState(e.target.value)}>
            <option value="">All</option>
            {Object.entries(CASE_STATE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </span>
      )}
      bodyClassName="space-y-4"
    >
      {error ? (
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-sm text-ipc-ink" role="alert"><StatusWord tone="bad" strong={false}>{error}</StatusWord></p>
          <button type="button" className="btn-secondary px-3 py-1 text-sm" disabled={loading} onClick={() => load(failedAfter)}>Try again</button>
        </div>
      ) : null}
      {loading && items.length === 0 ? <p className="text-sm text-ipc-muted" role="status">Loading flagged cases</p> : null}
      {loaded && !error && items.length === 0 ? <p className="text-sm text-ipc-muted">Nothing flagged. A case opens when someone answers that a change was not expected.</p> : null}
      {items.length > 0 ? (
        <ul className="divide-y divide-ipc-border">
          {items.map((c) => (
            <li key={c.caseId} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-3">
              <StatusWord tone={CASE_TONE[c.state] ?? 'off'}>{CASE_STATE_LABEL[c.state] ?? c.state}</StatusWord>
              <Link className="font-semibold text-ipc-light hover:underline" to={activityPath(customerId, `flagged/${encodeURIComponent(c.caseId)}`)}>
                {caseLine(c)}, updated {formatWhen(c.updatedAt)}
              </Link>
              {c.reportedByAdmin === true ? <span className="ml-2"><ReportedByAdmin /></span> : null}
            </li>
          ))}
        </ul>
      ) : null}
      {cursor && !error ? <button type="button" className="btn-secondary" disabled={loading} onClick={() => load(cursor)}>Load more</button> : null}
    </SectionCard>
  );
}

export function WatchCaseDetail({ customerId }) {
  const { caseId } = useParams();
  const [data, setData] = useState(null);
  const [outcome, setOutcome] = useState('');
  const [note, setNote] = useState('');
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  // The case whose page is showing; a response for any other case is dropped.
  const keyRef = useRef(null);
  const key = `${customerId}|${caseId}`;
  const outcomeId = useId();
  const noteId = useId();
  useUnsavedChanges(note.trim() !== '');

  const load = useCallback(async () => {
    try {
      const d = await api.getWatchCase(customerId, caseId);
      if (keyRef.current !== key) return false;
      setData(d);
      return true;
    } catch (e) {
      if (keyRef.current === key) setError(e?.message || 'The case could not be loaded.');
      return false;
    }
  }, [customerId, caseId, key]);
  useEffect(() => {
    keyRef.current = key;
    setData(null);
    setOutcome('');
    setNote('');
    setStatus(null);
    setError(null);
    setBusy(false);
    load();
    return () => { keyRef.current = null; };
  }, [key, load]);

  async function change(body) {
    setBusy(true);
    setError(null);
    setStatus(null);
    try {
      const r = await api.updateWatchCase(customerId, caseId, body);
      if (keyRef.current !== key) return;
      setNote('');
      setStatus(r?.notified === false ? NOT_TOLD_TEXT : (r?.notified === true ? TOLD_TEXT : 'Case updated.'));
      await load();
    } catch (e) {
      if (keyRef.current !== key) return;
      // Someone else moved the case first: show where it stands now, then say why the change was refused.
      if (e?.status === 409) await load();
      if (keyRef.current === key) setError(changeError(e));
    } finally {
      if (keyRef.current === key) setBusy(false);
    }
  }

  if (!data && error) {
    return (
      <div className="card space-y-3">
        <div className="text-ipc-ink" role="alert"><StatusWord tone="bad" strong={false}>{error}</StatusWord></div>
        <button type="button" className="btn-secondary" onClick={() => { setError(null); load(); }}>Try again</button>
      </div>
    );
  }
  if (!data) return <div className="py-12 text-center text-ipc-muted" role="status">Loading the case</div>;
  const c = data.case;
  const ev = data.event;
  const trimmed = note.trim();
  // The portal moves a case only open to acknowledged, and open or acknowledged to resolved.
  const canAcknowledge = c.state === 'open';
  const canResolve = c.state === 'open' || c.state === 'acknowledged';
  return (
    <section className="card space-y-5">
      <Link to={activityPath(customerId, 'flagged')} className="inline-flex items-center gap-1 text-sm font-semibold text-ipc-light hover:underline">Back to Flagged</Link>
      <div className="space-y-1">
        <h2>Flagged: {caseLine(c)}</h2>
        {c.reportedByAdmin === true ? <div><ReportedByAdmin /></div> : null}
        {ev ? (
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <SeverityWord severity={ev.severity} />
            <Link className="font-semibold text-ipc-light hover:underline" to={activityPath(customerId, `events/${encodeURIComponent(ev.eventId)}`)}>{ev.what}</Link>
            <span className="text-ipc-muted">{ev.actor?.displayName ?? 'Unknown actor'}, {formatWhen(ev.activityDateTime)}</span>
          </div>
        ) : <p className="text-sm text-ipc-muted">The event behind this case is no longer available.</p>}
        {ev ? <WatchEventDetails details={ev.details} /> : null}
      </div>
      <ol className="space-y-2 text-sm">
        {(c.history ?? []).map((h, i) => (
          <li key={`${h.at}-${i}`} className="border-l-2 border-ipc-border pl-3">
            <div className="font-medium">{CASE_STATE_LABEL[h.state] ?? h.state} by {h.actor ?? 'unknown'}, {formatWhen(h.at)}</div>
            {h.reportedByAdmin === true ? <div className="mt-0.5"><ReportedByAdmin /></div> : null}
            {h.note ? <div className="break-words text-ipc-ink">Note: {h.note}</div> : null}
          </li>
        ))}
      </ol>
      {canAcknowledge || canResolve ? (
        <div className="space-y-3">
          <div className="text-sm">
            <label className="label" htmlFor={noteId}>Note (optional)</label>
            <textarea id={noteId} className="input" maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
          <div className="flex flex-wrap items-end gap-3">
            {canAcknowledge ? <button type="button" className="btn-secondary" disabled={busy} onClick={() => change({ state: 'acknowledged', ...(trimmed ? { note: trimmed } : {}) })}>Acknowledge</button> : null}
            <div className="w-60 text-sm">
              <label className="label" htmlFor={outcomeId}>Outcome</label>
              <select id={outcomeId} className="input" value={outcome} onChange={(e) => setOutcome(e.target.value)}>
                <option value="">Choose an outcome</option>
                {Object.entries(CASE_OUTCOME_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <button type="button" className="btn-primary" disabled={busy || !outcome} onClick={() => change({ state: 'resolved', outcome, ...(trimmed ? { note: trimmed } : {}) })}>Resolve</button>
          </div>
          <p className="text-[13px] text-ipc-muted">Every change is sent to your guardians. {OWN_CHANGE_TEXT}</p>
        </div>
      ) : null}
      {status ? <p className="text-sm text-ipc-ink" role="status">{status}</p> : null}
      {error ? <p className="text-sm text-ipc-ink" role="alert"><StatusWord tone="bad" strong={false}>{error}</StatusWord></p> : null}
    </section>
  );
}
